import { Suspense, useMemo } from 'react';
import { Vector3 } from 'three';
import { useShallow } from 'zustand/shallow';

import { useCreatePanelDimensioning } from 'app/modules/wallforming/components/scenes/objects/dimensions/hooks/useCreatePanelDimensioning';
import { PanelDimensionBorder } from 'app/modules/wallforming/components/scenes/objects/dimensions/PanelDimensionBorder';
import { PanelDimensionPlane } from 'app/modules/wallforming/components/scenes/objects/dimensions/PanelDimensionPlane';
import { PanelDimensionTexts } from 'app/modules/wallforming/components/scenes/objects/dimensions/PanelDimensionTexts';
import { usePlacementStore } from 'app/modules/wallforming/components/state-management/placement';

interface PanelDimensionProps {
    mid: Vector3;
    floorZ: number;
    selectedPoiIndex: number;
}

export const PanelDimension = ({ mid, floorZ, selectedPoiIndex }: PanelDimensionProps) => {
    const { workingPlane, articleColliders, instanceToGroupMap, articleGroups } = usePlacementStore(
        useShallow((state) => ({
            articleColliders: state.colliders['article'],
            workingPlane: state.workingPlane,
            instanceToGroupMap: state.instanceToGroupMap,
            articleGroups: state.articleGroups
        }))
    );

    const groupedInstanceIds = useMemo(() => new Set(Object.keys(instanceToGroupMap)), [instanceToGroupMap]);

    const groupColors = useMemo(
        () => Object.fromEntries(Object.entries(articleGroups).map(([id, g]) => [id, g.color])),
        [articleGroups]
    );

    const { panels } = useCreatePanelDimensioning({
        articleColliders,
        floorZ,
        mid,
        planeNormal: workingPlane?.normal,
        selectedPoiIndex,
        groupedInstanceIds,
        instanceToGroupMap
    });

    if (panels.length === 0) {
        return null;
    }

    return (
        <Suspense>
            <PanelDimensionPlane groupColors={groupColors} panels={panels} />
            <PanelDimensionTexts panels={panels} />
            <PanelDimensionBorder panels={panels} />
        </Suspense>
    );
};
